import { type DqFuelType } from './carInfoFuelTypeMap';
import {
  SPEC_ID_BRANSLE_BENSIN,
  SPEC_ID_BRANSLE_DIESEL,
  SPEC_ID_BRANSLE_ELECTRIC,
  SPEC_ID_BRANSLE_ETHANOL,
  SPEC_ID_BRANSLE_GAS,
} from './carInfoSpecIds';

export type { DqFuelType };

interface CarInfoSpecAttr {
  id?: string;
  values?: unknown[];
}

interface CarInfoFuelInput {
  engineType?: string | null;
  specifications?: unknown[];
}

function flattenSpecAttrs(specifications: unknown[] | undefined): CarInfoSpecAttr[] {
  if (!specifications?.length) return [];
  const out: CarInfoSpecAttr[] = [];
  for (const cat of specifications as { attributes?: Record<string, CarInfoSpecAttr> }[]) {
    const attrs = cat?.attributes;
    if (!attrs || typeof attrs !== 'object') continue;
    for (const val of Object.values(attrs)) {
      if (val?.id != null) out.push(val);
    }
  }
  return out;
}

function isJaValue(raw: unknown): boolean {
  const v = String(raw ?? '').trim().toLowerCase();
  return v === 'ja' || v === 'yes' || v === 'true' || v === 'y' || v === '1';
}

/** Klassificera svensk bränsle-/motortext till kanonisk kategori. */
export function classifyFuelCategoryFromText(text?: string | null): DqFuelType | null {
  const s = (text ?? '').toLowerCase().trim();
  if (!s) return null;
  if (s.includes('laddhybrid') || s.includes('plug-in') || s.includes('plugin') || s.includes('phev')) {
    return 'Laddhybrid';
  }
  if (s.includes('hybrid')) return 'Hybrid';
  if (s.includes('diesel')) return 'Diesel';
  if (s.includes('etanol') || s.includes('e85')) return 'Etanol';
  if (s.includes('naturgas') || s.includes('fordonsgas') || s.includes('cng') || s.includes('lpg') || s.includes('gas')) {
    return 'Gas';
  }
  if (s.includes('bensin') || s.includes('petrol')) return 'Bensin';
  if (s === 'el' || /(^|[^a-zåäö])el([^a-zåäö]|$)/.test(s) || s.includes('eldrift') || s.includes('electric')) {
    return 'El';
  }
  return null;
}

function activeFuelFlagIds(specifications: unknown[] | undefined): Set<string> {
  const ids = new Set<string>();
  for (const attr of flattenSpecAttrs(specifications)) {
    if (isJaValue(attr.values?.[0])) ids.add(String(attr.id ?? ''));
  }
  return ids;
}

/** Laddhybrid = Bensin(141) + El(143). Ren el = 143 utan 141. */
export function classifyFuelCategoryFromFlags(specifications: unknown[] | undefined): DqFuelType | null {
  const ids = activeFuelFlagIds(specifications);
  if (ids.has(SPEC_ID_BRANSLE_DIESEL)) return 'Diesel';
  if (ids.has(SPEC_ID_BRANSLE_GAS)) return 'Gas';
  if (ids.has(SPEC_ID_BRANSLE_ETHANOL)) return 'Etanol';
  if (ids.has(SPEC_ID_BRANSLE_ELECTRIC) && ids.has(SPEC_ID_BRANSLE_BENSIN)) return 'Laddhybrid';
  if (ids.has(SPEC_ID_BRANSLE_ELECTRIC)) return 'El';
  if (ids.has(SPEC_ID_BRANSLE_BENSIN)) return 'Bensin';
  return null;
}

/** Kanonisk kategori (för prefill av dropdown). Primärt engine_type-text, annars flaggor. */
export function resolveFuelTypeFromCarInfo(input: CarInfoFuelInput): DqFuelType | null {
  return classifyFuelCategoryFromText(input.engineType) ?? classifyFuelCategoryFromFlags(input.specifications);
}
